import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import { useSelector } from 'hooks/redux'

import { getIsLoading } from 'selectors/application'
import { GET_DONOR_FEATURE_DATA } from 'actions/feature/features'

import { Table, Header, Loading } from 'components/patterns'
import { bitBytePairLabel, getTheRange } from 'components/feature/utils/bitGrid'

import strings from 'utils/strings'
import { FUNCTION, QUERY, OUTPUT } from 'utils/constants'
import { isString } from 'utils/types'

import {
  ReviewHeader,
  ConflictCell,
  StyledBadge,
} from './BatchImportReview.styles'

const typeToDisplayStringMap = {
  [FUNCTION]: strings.function,
  [OUTPUT]: strings.output,
  [QUERY]: strings.query,
}

const getColumnValueString = (conflict) => {
  if (!conflict) {
    return null
  }
  const {
    description,
    connector,
    inputConfig,
    location,
    startBit,
    numberOfBits,
  } = conflict

  if (numberOfBits) {
    const { startBit: start, endBit } = getTheRange(numberOfBits, startBit)
    return `${strings.controls} ${description}: ${bitBytePairLabel(
      start,
      endBit
    )}`
  }
  if (inputConfig) {
    return `${description}: ${inputConfig}: ${connector}-${location}`
  }
  // PBI-492348: Native (SERVICE) outputs have no connector/location — show
  // "Controls Native Output [description]" instead of "Controls [desc] : undefined-undefined"
  if (!connector && !location) {
    return `${strings.controls} ${strings.native_output} ${description}`
  }
  return `${strings.controls} ${description}: ${connector}-${location}`
}

const buildColumns = (selectedPartNumber, currentPartNumber) => [
  {
    dataIndex: 'conflict',
    key: (record) => `${record.conflict}-badge-row`,
    align: 'center',
    render: (conflict, record) => {
      if (conflict) {
        return (
          <StyledBadge text={strings.conflict.toUpperCase()} type="warning" />
        )
      }
      if (record.orphan) {
        return <StyledBadge text={strings.orphan.toUpperCase()} type="alert" />
      }
      return null
    },
  },
  {
    title: strings.object_type,
    dataIndex: 'type',
    key: 'uri',
    render: (type) => {
      if (typeToDisplayStringMap[type]) {
        return typeToDisplayStringMap[type]
      }
      return null
    },
    width: 120,
  },
  {
    title: strings.description,
    dataIndex: 'description',
    key: 'description',
    render: (text) => text,
  },
  {
    title: selectedPartNumber
      ? `${strings.conflict} (${selectedPartNumber})`
      : strings.conflict_source,
    dataIndex: 'conflict',
    key: 'conflict',
    render: (conflict, record) => {
      if (record.orphan && record.carriedBy) {
        return `${strings.carried_by} "${record.carriedBy}"`
      }
      return getColumnValueString(conflict)
    },
  },
  {
    title: currentPartNumber
      ? `${strings.conflict} (${currentPartNumber})`
      : strings.conflict_current_feature,
    dataIndex: 'offender',
    key: 'offender',
    render: (text, { conflict }) => {
      if (conflict) {
        if (isString(text)) {
          return (
            <ConflictCell>
              <span>{text}:</span> <span>{getColumnValueString(conflict)}</span>
            </ConflictCell>
          )
        }
        const { label, ...restOffender } = text
        return (
          <ConflictCell>
            <span>{label}:</span>{' '}
            <span>{getColumnValueString(restOffender)}</span>
          </ConflictCell>
        )
      }
      return null
    },
  },
]

const BatchImportReview = ({
  selectedPartNumber,
  currentPartNumber,
  selectedFeatureId,
  logicReviewRowSelectionProps,
  data,
  featureDescription,
}) => {
  const isLoading = useSelector(getIsLoading, {
    asyncKeys: [GET_DONOR_FEATURE_DATA],
  })

  const columns = buildColumns(selectedPartNumber, currentPartNumber)

  return isLoading ? (
    <Loading contained size="sm" />
  ) : (
    <>
      <ReviewHeader>
        <Header type="h3">{`${strings.import_feature} "${featureDescription}"`}</Header>
        <Header type="h4">
          <Link to={`/features/${selectedFeatureId}`} target="_blank">
            {selectedPartNumber}
          </Link>
        </Header>
      </ReviewHeader>
      <Table
        columns={columns}
        dataSource={data}
        rowSelection={{
          columnWidth: 24,
          ...logicReviewRowSelectionProps,
        }}
        rowKey="uri"
        defaultExpandAllRows
        expandable={{
          expandIconColumnIndex: 2,
          fixed: 'right',
        }}
      />
    </>
  )
}

BatchImportReview.propTypes = {
  selectedFeatureId: PropTypes.string,
  selectedPartNumber: PropTypes.string,
  currentPartNumber: PropTypes.string,
  logicReviewRowSelectionProps: PropTypes.object,
  data: PropTypes.array,
  featureDescription: PropTypes.string,
}

export default BatchImportReview
